import type { CategoryExecutor } from './executor.types';

export const executeFormsNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};

  if (type.endsWith('.render-form') || type.endsWith('.load-form')) {
    return { status: 'pause' };
  }

  if (type.endsWith('.submit-form')) {
    // When the Angular page already persisted the entity (opportunityId / formSubmitted),
    // skip a second POST — the host UI owns the API call.
    if (context.payload['formSubmitted'] || context.payload['opportunityId'] || context.payload['id']) {
      return {
        status: 'success',
        output: {
          formSubmitted: true,
          opportunityId: context.payload['opportunityId'] ?? context.payload['id'],
        },
      };
    }
    const endpoint =
      typeof config['apiEndpoint'] === 'string' ? config['apiEndpoint'] : undefined;
    const method =
      typeof config['apiMethod'] === 'string' ? config['apiMethod'] : 'POST';
    if (endpoint) {
      try {
        const result = await context.proxy.invoke({
          category: 'forms',
          operation: 'submit-form',
          endpoint,
          method,
          body: context.payload,
          headers: (config['apiHeaders'] as Record<string, string>) ?? undefined,
        });
        return { status: 'success', output: { formSubmitResult: result } };
      } catch (error) {
        return {
          status: 'failure',
          message: error instanceof Error ? error.message : 'Form submit failed',
          code: 'FORM_SUBMIT_FAILED',
        };
      }
    }
    return { status: 'success', output: { formSubmitted: true } };
  }

  if (type.endsWith('.save-draft')) {
    return { status: 'success', output: { draftSavedAt: new Date().toISOString() } };
  }

  if (type.endsWith('.validate-form')) {
    const rule = typeof config['rule'] === 'string' ? config['rule'] : undefined;
    if (rule) {
      try {
        const ok = Boolean(context.expressions.evaluate(rule, context.payload));
        if (!ok) {
          return {
            status: 'failure',
            message:
              typeof config['errorMessage'] === 'string'
                ? config['errorMessage']
                : 'Form validation failed',
          };
        }
      } catch (error) {
        return {
          status: 'failure',
          message: error instanceof Error ? error.message : 'Form validation failed',
        };
      }
    }
    return { status: 'success' };
  }

  if (type.endsWith('.reset-form')) {
    return { status: 'success', output: { formReset: true } };
  }

  if (type.endsWith('.pre-fill-form') || type.endsWith('.field-mapping')) {
    const mapping = config['fieldMapping'];
    const output: Record<string, unknown> = {};
    if (Array.isArray(mapping)) {
      for (const row of mapping) {
        if (row && typeof row === 'object' && 'key' in row && 'value' in row) {
          const r = row as { key: string; value: string };
          output[r.key] = context.payload[r.value] ?? r.value;
        }
      }
    } else if (mapping && typeof mapping === 'object') {
      for (const [k, v] of Object.entries(mapping as Record<string, string>)) {
        output[k] = context.payload[v] ?? v;
      }
    }
    return { status: 'success', output };
  }

  if (
    type.endsWith('.hide-fields') ||
    type.endsWith('.show-fields') ||
    type.endsWith('.enable-fields') ||
    type.endsWith('.disable-fields')
  ) {
    return {
      status: 'success',
      output: {
        fieldVisibility: {
          action: type.split('.').pop(),
          targetFields: config['targetFields'],
          condition: config['condition'],
        },
      },
    };
  }

  if (type.endsWith('.field-transformation')) {
    const source = typeof config['sourceField'] === 'string' ? config['sourceField'] : undefined;
    const expr =
      typeof config['transformExpression'] === 'string'
        ? config['transformExpression']
        : undefined;
    if (source && expr) {
      try {
        const result = context.expressions.evaluate(expr, {
          ...context.payload,
          value: context.payload[source],
        });
        return { status: 'success', output: { [source]: result } };
      } catch (error) {
        return {
          status: 'failure',
          message: error instanceof Error ? error.message : 'Transform failed',
        };
      }
    }
  }

  if (type.endsWith('.custom-form-script') && typeof config['script'] === 'string') {
    try {
      context.expressions.evaluate(config['script'], context.payload);
    } catch {
      // ignore script side-effects failures in v1
    }
  }

  return { status: 'success' };
};
